/**
 * RocketTalk - Main App Component
 * 
 * CRITICAL FIX: Socket is now managed at App level, not MainApp level.
 * Previously the socket was initialized in MainApp and disconnected
 * on unmount — navigating to /map unmounted MainApp, killing the socket
 * and breaking PTT audio in both directions.
 * 
 * Now: socket connects once when user authenticates, persists across ALL
 * routes (/map, /profile, /admin, etc.), disconnects only on logout.
 */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useStore from './store/useStore';
import useSocket from './hooks/useSocket';
import LoginPage from './components/LoginPage';
import MainApp from './components/MainApp';
import ProfilePage from './components/ProfilePage';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import UserManagement from './components/admin/UserManagement';
import ChannelManagement from './components/admin/ChannelManagement';
import ActivityLog from './components/admin/ActivityLog';
import AdminSettings from './components/admin/Settings';
import RoleManagement from './components/admin/RoleManagement';
import Webhooks from './components/admin/Webhooks';
import Analytics from './components/admin/Analytics';
import Broadcasts from './components/admin/Broadcasts';
import MapPage from './components/MapPage';
import MissionsPage from './components/MissionsPage';
import ReplayPage from './components/ReplayPage';
import ActivityTimeline from './components/ActivityTimeline';
import VideoCallPage from './components/VideoCallPage';
import AlertsPanel from './components/admin/AlertsPanel';
import TranscriptionsPanel from './components/admin/Transcriptions';
import OrganizationsAdmin from './components/admin/Organizations';
import ToastContainer from './components/Toast';
import ConfirmDialog from './components/ConfirmDialog';
import axios from 'axios';

function ProtectedRoute({ children }) {
  const isAuthenticated = useStore(s => s.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const isAuthenticated = useStore(s => s.isAuthenticated);
  const role = useStore(s => s.user?.role);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (role && role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

// Must match server-side viewLocations: Administrador + Supervisor de Operaciones (2026-07-01)
const MAP_ROLES = ['Administrador', 'Supervisor de Operaciones'];
function MapRoute({ children }) {
  const isAuthenticated = useStore(s => s.isAuthenticated);
  const user = useStore(s => s.user);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user && user.role !== 'admin' && !MAP_ROLES.includes(user.customRole?.name)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

let interceptorInstalled = false;
function installAxiosInterceptor() {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    res => res,
    err => {
      if (err.response?.status === 401) {
        const { logout, token } = useStore.getState();
        if (token) {
          console.warn('[Auth] 401 — token expired, logging out');
          logout();
          window.location.href = '/login';
        }
      }
      return Promise.reject(err);
    }
  );
}

// ── Socket keeper — lives at App level so it survives route changes ──────
function SocketKeeper() {
  const token = useStore(s => s.token);
  const userId = useStore(s => s.user?.id);
  const { connect, disconnect } = useSocket();
  const setUser = useStore(s => s.setUser);
  const logout = useStore(s => s.logout);

  useEffect(() => {
    installAxiosInterceptor();
  }, []);

  // Hydrate user on token load
  useEffect(() => {
    if (!token) return;
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.get('/api/auth/me')
      .then(res => setUser(res.data))
      .catch(() => logout());
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Connect socket as soon as token is available — no need to wait for userId
  // The socket authenticates itself via the JWT token in the auth handshake
  useEffect(() => {
    if (!token) return;
    console.log('[App] Connecting socket (token present)');
    connect();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Disconnect socket on logout (token becomes null)
  useEffect(() => {
    if (!token) {
      console.log('[App] Token cleared — disconnecting socket');
      disconnect();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return null; // renders nothing, just manages socket lifecycle
}

function PoweredBy() {
  return (
    <div style={{
      position: 'fixed', bottom: 6, left: '50%', transform: 'translateX(-50%)',
      display: 'inline-flex', alignItems: 'center', gap: 8,
      fontSize: '11px', padding: '4px 12px',
      pointerEvents: 'none', zIndex: 9999,
      letterSpacing: '0.04em', userSelect: 'none',
      color: '#48484A',
      fontFamily: 'var(--rt-font-sans)',
      background: 'rgba(255,255,255,0.62)',
      WebkitBackdropFilter: 'saturate(180%) blur(20px)',
      backdropFilter: 'saturate(180%) blur(20px)',
      border: '1px solid rgba(255,255,255,0.7)',
      borderRadius: 999,
      boxShadow: '0 2px 6px rgba(0,0,0,.06)',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34C759', boxShadow: '0 0 8px #34C759' }} />
      Powered by{' '}
      <span style={{ color: '#1D1D1F', fontWeight: 700 }}>Rocket Operations</span>
      <span style={{ color: '#AEAEB2' }}>§</span>
      <span style={{ color: '#1D1D1F', fontWeight: 700 }}>Muesser &amp; Co.</span>
    </div>
  );
}

export default function App() {
  const isAuthenticated = useStore(s => s.isAuthenticated);

  return (
    <BrowserRouter>
      {/* Socket keeper persists across ALL route changes */}
      <SocketKeeper />

      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
        } />

        <Route path="/profile" element={
          <ProtectedRoute><ProfilePage /></ProtectedRoute>
        } />

        <Route path="/map" element={
          <MapRoute><MapPage /></MapRoute>
        } />

        <Route path="/missions" element={
          <ProtectedRoute><MissionsPage /></ProtectedRoute>
        } />

        <Route path="/replay" element={
          <ProtectedRoute><ReplayPage /></ProtectedRoute>
        } />

        <Route path="/activity" element={
          <ProtectedRoute><ActivityTimeline /></ProtectedRoute>
        } />

        <Route path="/video/:channelId" element={
          <ProtectedRoute><VideoCallPage /></ProtectedRoute>
        } />

        <Route path="/admin" element={
          <AdminRoute><AdminLayout /></AdminRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="channels" element={<ChannelManagement />} />
          <Route path="roles" element={<RoleManagement />} />
          <Route path="activity" element={<ActivityLog />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="webhooks" element={<Webhooks />} />
          <Route path="broadcasts" element={<Broadcasts />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="alerts" element={<AlertsPanel />} />
          <Route path="transcriptions" element={<TranscriptionsPanel />} />
          <Route path="organizations" element={<OrganizationsAdmin />} />
        </Route>

        <Route path="/*" element={
          <ProtectedRoute><MainApp /></ProtectedRoute>
        } />
      </Routes>

      <ToastContainer />
      <ConfirmDialog />
      <PoweredBy />
    </BrowserRouter>
  );
}
